import { signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { SymbolStoreModel } from "@bitwarden/web-vault/app/models/store/symbol.store.model";
import {
  copy,
  clone,
  fromStoreModel,
  SymbolView,
} from "@bitwarden/web-vault/app/models/view/symbol/symbol.view";
import { StoreModelNames } from "@bitwarden/web-vault/app/services/dali/type/dali.type";
import { StoreModelCollection } from "@bitwarden/web-vault/app/services/store/model.collection.abstraction";

export class SymbolsStoreCollection extends StoreModelCollection {
  protected type: StoreModelNames = "SymbolStoreModel";
  protected models: Map<string, SymbolStoreModel> = new Map<string, SymbolStoreModel>();
  private _loadedCollection = signal<SymbolView[] | null>(null);
  readonly loadedCollection = this._loadedCollection.asReadonly();
  readonly loadedCollection$ = toObservable(this._loadedCollection);
  private _collection = signal<SymbolView[]>(null);
  readonly collection = this._collection.asReadonly();

  merge(views: SymbolView[]): void {
    const loadedViews = this.loadedCollection();

    if (!loadedViews || loadedViews.length === 0 || !views || views.length === 0) {
      this._collection.set(views);
      return;
    }

    const map = loadedViews.reduce((m, v) => {
      const view = clone(v);
      Object.values(v.sources).forEach((c) => {
        m.set(c, view);
      });
      m.set(view.code, view);
      return m;
    }, new Map<string, SymbolView>());

    const mergedViews = views.reduce((acc, v) => {
      let view = v;
      if (map.has(v.code)) {
        view = map.get(v.code);
        const vaultName = view.name;
        const vaultGroup = view.group;
        copy(view, v);
        if (vaultName) {
          view.name = vaultName;
        }
        view.group = vaultGroup;
      }
      return acc.set(view.code, view);
    }, new Map<string, SymbolView>());

    if (mergedViews.size !== 0) {
      this._collection.set([...mergedViews.values()]);
    }
  }

  triggerChanges(): void {
    const views: SymbolView[] = [];
    this.models.forEach((model) => {
      views.push(this.deepFreeze(fromStoreModel(model)));
    });
    this._loadedCollection.set(views);
  }

  clear(): void {
    this.models.clear();
    this._loadedCollection.set(null);
    this._collection.set(null);
  }
}
